import React, { useState, useEffect, useRef } from 'react';
import { Plus, User, FolderOpen, Palette, Heart, Phone, HelpCircle, ChevronUp, MessageSquareText, Laptop } from 'lucide-react';

const SkillsPage = () => {
  const programmingLanguages = [
    'Java', 'React.js', 'Node.js', 'Express.js', 'SQL'
  ];

  const techFrameworks = [
    'Frappe', 'Numpy', 'Pandas', 'Matplotlib', 'Git', 'MariaDB', 'MongoDB Atlas'
  ];

  const concepts = [
    'Data Structures', 'Algorithms', 'Object-Oriented Programming', 'Database Management System', 'AI'
  ];

  const navItems = [
    { icon: User, label: 'Me', active: false },
    { icon: FolderOpen, label: 'Projects', active: false },
    { icon: Palette, label: 'Skills', active: true },
    { icon: Heart, label: 'Fun', active: false },
    { icon: Phone, label: 'Contact', active: false },
    { icon: HelpCircle, label: '', active: false }
  ];

  const [messages, setMessages] = useState([]);
  const [currentInput, setCurrentInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const suggestedPrompts = [
    "What are your core programming languages?",
    "Tell me about your experience with frameworks.",
    "Can you explain your understanding of data structures?"
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const generateSkillsResponse = (userPrompt) => {
    let responseContent = "";

    if (userPrompt.includes("programming languages")) {
      responseContent += "My core programming languages include: ";
      responseContent += programmingLanguages.map(skill => `\`${skill}\``).join(' ') + ". ";
      responseContent += "I'm proficient in these for various applications, from system development to web interfaces.";
    } else if (userPrompt.includes("frameworks")) {
      responseContent += "I have experience with a range of frameworks and technologies such as: ";
      responseContent += techFrameworks.map(skill => `\`${skill}\``).join(' ') + ". ";
      responseContent += "These enable me to build robust and scalable solutions.";
    } else if (userPrompt.includes("data structures")) {
      responseContent += "My understanding of core concepts like Data Structures and Algorithms is strong. ";
      responseContent += "I apply these principles to write efficient and optimized code, crucial for problem-solving.";
    } else {
      responseContent += "Certainly! Here's a comprehensive overview of my technical skills and expertise:\n\n";
      responseContent += "### <MessageSquareText size={20} /> Languages\n";
      responseContent += programmingLanguages.map(skill => `\`${skill}\``).join(' ') + ".\n\n";
      responseContent += "### <Laptop size={20} /> Technologies & Frameworks\n";
      responseContent += techFrameworks.map(skill => `\`${skill}\``).join(' ') + ".\n\n";
      responseContent += "### <Brain size={20} /> Core Concepts\n";
      responseContent += concepts.map(skill => `\`${skill}\``).join(' ') + ".\n\n";
      responseContent += "This diverse skill set allows me to tackle various challenges across different domains, from building robust applications to analyzing complex data.";
    }
    return responseContent;
  };

  const sendMessage = (promptText) => {
    setMessages((prevMessages) => [...prevMessages, { type: 'user', content: promptText }]);
    setCurrentInput('');
    setIsTyping(true);

    setTimeout(() => {
      const aiResponse = generateSkillsResponse(promptText);
      setMessages((prevMessages) => [...prevMessages, { type: 'ai', content: aiResponse }]);
      setIsTyping(false);
    }, 2000);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const autoPromptText = "Can you provide a detailed list of your technical skills?";

    let charIndex = 0;
    const typingInterval = setInterval(() => {
      if (charIndex <= autoPromptText.length) {
        setCurrentInput(autoPromptText.substring(0, charIndex));
        charIndex++;
      } else {
        clearInterval(typingInterval);
        sendMessage(autoPromptText);
      }
    }, 50);

    return () => {
      clearInterval(typingInterval);
    };
  }, []);

  const renderContent = (content) => {
    return content.split('\n').map((line, lineIndex) => {
      if (line.startsWith('## ')) {
        return <h2 key={lineIndex} className="text-xl sm:text-2xl md:text-3xl font-bold mt-2 sm:mt-3 mb-2 sm:mb-3">{line.substring(3)}</h2>;
      }
      if (line.startsWith('### ')) {
        const match = line.match(/### <(.*?) size={(\d+)} \/> (.*)/);
        if (match) {
          const IconComponent = require('lucide-react')[match[1]];
          const iconSize = parseInt(match[2]);
          const textContent = match[3];

          return (
            <div key={lineIndex} className="flex items-center mt-2 sm:mt-3 mb-1 sm:mb-2">
              {IconComponent && <IconComponent size={iconSize} className="mr-1 sm:mr-2" />}
              <h3 className="text-lg sm:text-xl font-semibold">{textContent}</h3>
            </div>
          );
        }
      }
      const formattedLine = line.replace(/`(.*?)`/g, '<span class="px-1.5 py-0.5 sm:px-2 sm:py-1 bg-gray-700 text-white text-xs sm:text-sm font-medium rounded-md inline-block mr-0.5 my-0.5 sm:mr-1 sm:my-1">$1</span>');
      return <p key={lineIndex} dangerouslySetInnerHTML={{ __html: formattedLine }} className="leading-normal sm:leading-relaxed text-sm sm:text-base"></p>;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-800 flex flex-col text-sm sm:text-base">
      <div className="flex-grow mx-4 sm:mx-8 md:mx-12 lg:mx-[10%] py-6 sm:py-8 md:py-10 lg:py-12 overflow-y-auto">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8 sm:mb-10 md:mb-12 space-y-3 sm:space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} gap-2 sm:gap-3`}
              >
                {message.type === 'ai' && (
                  <img
                    src="images/Siddharth_Singh_Khati.png"
                    alt="AI Profile"
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover self-start mb-1"
                  />
                )}
                <div
                  className={`max-w-[80%] sm:max-w-[70%] py-1.5 px-2.5 sm:py-2 sm:px-3 rounded-2xl sm:rounded-3xl text-sm sm:text-base text-justify ${
                    message.type === 'user'
                      ? 'bg-blue-900 text-white'
                      : 'text-gray-800'
                  }`}
                >
                  {renderContent(message.content)}
                </div>
                {message.type === 'user' && (
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-600 flex items-center justify-center text-white self-end mb-1">
                    You
                  </div>
                )}
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start gap-2 sm:gap-3">
                <img
                  src="images/Siddharth_Singh_Khati.png"
                  alt="AI Profile"
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover self-start mb-1"
                />
                <div className="max-w-[70%] p-3 sm:p-4 rounded-xl bg-white border border-gray-200">
                  <div className="flex items-center space-x-1 sm:space-x-1.5">
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-500 rounded-full animate-bounce delay-100"></span>
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-500 rounded-full animate-bounce delay-200"></span>
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-500 rounded-full animate-bounce delay-300"></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 left-0 right-0 bg-gray-50 pt-3 sm:pt-4 border-t border-gray-100">
        <div className="mx-4 sm:mx-8 md:mx-12 lg:mx-[10%] pb-6 sm:pb-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4 justify-start">
              {suggestedPrompts.map((prompt, index) => (
                <button
                  key={index}
                  onClick={() => sendMessage(prompt)}
                  className="flex items-center px-3 py-1.5 sm:px-4 sm:py-2 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-colors text-xs sm:text-sm"
                >
                  <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" />
                  {prompt}
                </button>
              ))}
            </div>

            <div className="relative">
              <input
                type="text"
                placeholder="Ask me anything"
                className="w-full px-3.5 py-2.5 sm:px-4 sm:py-3 bg-gray-100 border border-gray-200 rounded-full text-sm sm:text-base text-gray-700 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={currentInput}
                readOnly
              />
              <button className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-blue-600 text-white p-1.5 sm:p-2 rounded-full hover:bg-blue-700 transition-colors">
                <ChevronUp className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkillsPage;